import InputSelect from "core/inputs/input-select/InputSelect";
import _ from "lodash";
import { FORM_INFO } from "constants/forms";
import { FormattedMessage } from "react-intl";
import { Table } from "core/table/Table";
import { Typography } from "core/typography/Typography";
import { formatStaff, updatedByDataHelper } from "utils/modalsHelper";
import { useEffect, useState } from "react";
import {
  onAdd,
  onChange,
  onChangeInputManager,
  onChangeManager,
  onDelete,
  renderCommonBlock,
} from "utils/tableContentHelper";
import { MISSION_MANAGER_ROLES } from "access-roles/roles";
import { FILTERS_FOR_NAMES, MODAL_CONTENT } from "constants/constants";
import InputSearchList from "core/inputs/input-search/InputSearchList";
import { phoneSize } from "utils/screenSizes";
import { headerGenerator } from "utils/HeaderHelper";
import Proptypes from "prop-types";
import { useGeneralContext } from "api/directory/general/GeneralProvider";
import { useUserDatatrackContext } from "api/datatrack/UserDatatrackProvider";
import AlertPopup from "components/alert-popup/AlertPopup";
import { POPUP_STATUS } from "constants/constants";
import { InputDateModal } from "core/inputs/input-date/InputDateModal";
import { format } from "date-fns";

const ManageManagers = ({
  branches,
  getUpdatedValues,
  userManager,
  lang,
  setElementsToDelete,
  connectedUser,
  managersNames,
}) => {
  const headerParam = [
    { key: "manager", defaultMessage: "Manager", icon: "user" },
    {
      key: "branch",
      defaultMessage: "Filiale",
      icon: "user",
      minWidth: "18rem",
    },
    {
      key: "establishment_date",
      defaultMessage: "Date de prise en compte",
      icon: "calendar",
    },
    { key: "updated_by", defaultMessage: "Modifié par", icon: "user" },
    { key: "updated_at", defaultMessage: "le", icon: "calendar" },
  ];
  const header = headerGenerator(headerParam);
  const { getNamesByFilters, namesByFilters } = useGeneralContext();
  const [initialData, setInitialData] = useState(userManager);
  const [data, setData] = useState(userManager);
  const [formattedManager, setFormattedManager] = useState([]);
  const [staffList, setStaffList] = useState();
  const [managersFromIds, setManagersFromIds] = useState([]);
  const [formattedSearchedManagers, setFormattedSearchedManagers] = useState(
    []
  );
  const { apiErrorResponse } = useUserDatatrackContext();
  const [status, setStatus] = useState();
  const [code, setCode] = useState();

  useEffect(() => {
    if (!apiErrorResponse || Object.keys(apiErrorResponse).length === 0) return;
    const firstError = Object.values(apiErrorResponse)[0];
    const backCode =
      typeof firstError === "string" ? firstError : firstError?.code;
    if (backCode) {
      setCode(backCode);
      setStatus(POPUP_STATUS.ERROR);
      return;
    }
    const fieldFallback = {
      establishment_date: "api_error_establishment_date",
      manager_id: "api_error_mgr",
      branch_id: "api_error_branch_id",
    };
    const matchedField = Object.keys(fieldFallback).find(
      (field) => apiErrorResponse[field]
    );
    setCode(matchedField ? fieldFallback[matchedField] : "api_error_field");
    setStatus(POPUP_STATUS.ERROR);
  }, [apiErrorResponse]);

  useEffect(() => {
    if (userManager?.length && data?.length) {
      updatedByDataHelper({ data, setData, modalData: userManager });
    }
  }, [userManager]);

  useEffect(() => {
    const formattedSearched = formatStaff(namesByFilters || []);
    setFormattedSearchedManagers(formattedSearched);

    const currentIds = data.map((d) => d.manager_id);
    const usedManagers = currentIds
      .map(
        (id) =>
          managersFromIds.find((m) => m.id === id) ||
          managersNames.find((m) => m.id === id)
      )
      .filter(Boolean);

    const combinedStaff = _.uniqWith(
      [...usedManagers, ...namesByFilters],
      _.isEqual
    );
    setManagersFromIds(_.uniqWith(usedManagers, _.isEqual));
    setStaffList(combinedStaff);

    const formattedStaff = formatStaff(combinedStaff);
    setFormattedManager(formattedStaff);
  }, [managersNames, data, namesByFilters]);

  useEffect(() => {
    if (staffList?.length > 0) {
      const formattedUsers = formatStaff(staffList);
      setFormattedManager(
        _.chain(formattedUsers)
          .concat(formattedManager)
          .flattenDeep()
          .uniqWith(_.isEqual)
          .value()
      );
    }
  }, [staffList]);

  const formatUserBranches = ({ list, userBranches }) => {
    return userBranches.map((elm) => ({
      label: _.chain(list)
        .find((l) => l.id === elm)
        .get("name")
        .value(),
      value: elm,
    }));
  };
  useEffect(() => {
    if (initialData.length < userManager.length && userManager.length > 0) {
      setInitialData(userManager);
    }
  }, [initialData, userManager]);

  useEffect(() => {
    if (initialData.length > 0) {
      setData(initialData);
    }
  }, [initialData]);

  const formatContent = (content) =>
    content.length > 0
      ? content?.map((elm, index) => {
          const dataTestIndex = content.length - 1 - index;
          return {
            id: elm?.id,
            manager_id: (
              <InputSearchList
                errorMessage={
                  apiErrorResponse?.["manager_id"] && "api_error_manager_id"
                }
                isTable={true}
                limitChoice={1}
                onDelete={onDelete}
                options={formattedSearchedManagers}
                popup={FORM_INFO.manager.popup}
                selectedOptions={formattedManager.filter(
                  (br) => br.value === elm.manager_id
                )}
                minLength={3}
                getOptions={(search) =>
                  getNamesByFilters({
                    role: MISSION_MANAGER_ROLES,
                    types: [FILTERS_FOR_NAMES.PERSON],
                    search,
                  })
                }
                setValue={(val) => {
                  onChangeManager({
                    getUpdatedValues,
                    id: elm?.id,
                    key: "manager_id",
                    setData,
                    data,
                    val,
                    options: staffList,
                    managerId: elm?.manager_id,
                    connectedUser,
                  });
                }}
                onChangeInput={() =>
                  onChangeInputManager({
                    getUpdatedValues,
                    id: elm?.id,
                    setData,
                    data,
                  })
                }
                inputSearchPopupMaxHeight={150}
                dataTestId={`${MODAL_CONTENT.MANAGE_MANAGERS}_modal-manager-${dataTestIndex}`}
              />
            ),
            branch_id: (
              <InputSelect
                id="branch_id"
                value={
                  branches?.find((branch) => branch.id === elm.branch_id)?.id
                }
                options={formatUserBranches({
                  list: branches,
                  userBranches: _.chain(staffList)
                    .find((e) => e.id === elm.manager_id)
                    .get("assigned_branches", [])
                    .value(),
                })}
                name="branch_id"
                setValue={(name, val) =>
                  onChange({
                    getUpdatedValues,
                    id: elm?.id,
                    key: name,
                    setData,
                    data,
                    val,
                  })
                }
                dataTestId={`${MODAL_CONTENT.MANAGE_MANAGERS}_modal-branch-${dataTestIndex}`}
              />
            ),
            establishment_date: (
              <div
                data-testid={`${MODAL_CONTENT.MANAGE_MANAGERS}_modal-establishment_date-${dataTestIndex}`}
              >
                <InputDateModal
                  lang={lang}
                  elm={elm}
                  onChange={(date) => {
                    const formattedDate = date
                      ? format(date, "yyyy-MM-dd")
                      : "";
                    onChange({
                      getUpdatedValues,
                      id: elm?.id,
                      key: "establishment_date",
                      setData,
                      data,
                      val: formattedDate,
                    });
                  }}
                />
              </div>
            ),
            ...renderCommonBlock({
              data,
              getUpdatedValues,
              id: elm.id,
              onDelete,
              setData,
              lang,
              setElementsToDelete,
              updated_at: elm.updated_at,
              updated_by: elm?.updated_by,
              deleteBtnTestId: `${MODAL_CONTENT.MANAGE_MANAGERS}_modal-delete_btn-${dataTestIndex}`,
            }),
          };
        })
      : [];
  return (
    <>
      <button
        onClick={() => onAdd({ header, data, setData })}
        className="button-style"
        data-testid={`${MODAL_CONTENT.MANAGE_MANAGERS}_modal-add_manager_btn`}
      >
        <Typography
          className="link-1 violet "
          type="h2"
          label={
            <>
              +{" "}
              <FormattedMessage
                id="add_manager"
                defaultMessage="Ajouter un manager"
              />
            </>
          }
        />
      </button>
      <br />
      <div></div>
      <AlertPopup
        code={code}
        setCode={setCode}
        setStatus={setStatus}
        status={status}
      />
      <Table
        id="manager"
        header={header}
        content={formatContent(data)}
        tableOverflow={"auto"}
        tableWidth={phoneSize ? "max-content" : "100%"}
      />
    </>
  );
};

export default ManageManagers;

ManageManagers.propTypes = {
  branches: Proptypes.array,
  getUpdatedValues: Proptypes.func,
  userManager: Proptypes.array,
  lang: Proptypes.string,
  setElementsToDelete: Proptypes.func,
  connectedUser: Proptypes.object,
  managersNames: Proptypes.array,
};
